import { useState } from 'react';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';

import { Button } from '@/components/ui/button/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { copyToClipboard } from '@/lib/utils';

import { useEnable2FAMutation } from '../api/use-enable-2FA-mutation';
import type { ResEnable2FADTO } from '../my-profile.types';

export interface Modal2FAProps {
  openModal: boolean;
  isEnabled: boolean;
  onClose: () => void;
}
const Modal2FA = ({ openModal, onClose, isEnabled }: Modal2FAProps) => {
  const [generateNew, setGenerateNew] = useState<boolean>(false);
  const [userOtpFeature, setUserOtpFeature] = useState<ResEnable2FADTO>();

  const useEnableMutation = useEnable2FAMutation();

  const handleGenerateNew = async () => {
    try {
      const res = await useEnableMutation.mutateAsync();

      if (res) {
        setUserOtpFeature(res);
      }
    } catch (error) {
      toast.error('Login failed', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
      });
    }
  };
  const hanldeClose = () => {
    setGenerateNew(false);
    setUserOtpFeature(undefined);
    onClose();
  };

  return (
    <Dialog
      open={openModal}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          hanldeClose();
        }
      }}
      data-testid="modal-2fa"
    >
      <DialogContent className="max-w-xs rounded-lg bg-gray-100 p-2 md:max-w-md">
        <div className="rounded-lg bg-white p-6">
          {!isEnabled && !generateNew && (
            <div className="space-y-6">
              <div className="flex flex-col gap-4 py-4 text-center">
                <DialogTitle className="text-brand-dark text-3xl font-bold">
                  Enable Two-Factor Authentication?
                </DialogTitle>
                <DialogDescription className="text-base text-gray-600">
                  To enhance the security of your account, we recommend enabling two-factor
                  authentication (2FA). This process will add an extra layer of security when you
                  log in.
                </DialogDescription>
              </div>
              <hr />
              <DialogFooter>
                <div className="grid w-full grid-cols-2 items-center justify-between gap-2">
                  <Button
                    className="bg-brand-blue hover:bg-brand-blue/90 w-full cursor-pointer"
                    onClick={() => {
                      handleGenerateNew();
                      setGenerateNew(true);
                    }}
                    disabled={useEnableMutation.isPending}
                  >
                    {useEnableMutation.isPending ? 'On Progress...' : 'Yes'}
                  </Button>
                  <Button
                    className="w-full cursor-pointer"
                    variant={'destructive'}
                    onClick={hanldeClose}
                  >
                    Cancel
                  </Button>
                </div>
              </DialogFooter>
            </div>
          )}
          {generateNew && userOtpFeature && (
            <div className="space-y-3">
              <DialogDescription className="text-base text-gray-600">
                1. Open/Install your preferred 2FA client on your phone (such as Google
                Authenticator)
              </DialogDescription>
              <DialogDescription className="text-base text-gray-600">
                2. Scan this barcode below
              </DialogDescription>
              <div className="flex items-center justify-center">
                <QRCode
                  size={200}
                  style={{ maxWidth: '100%', width: '200px', height: '200px' }}
                  value={userOtpFeature?.data.totp_qr ? userOtpFeature.data.totp_qr : '-'}
                  viewBox={`0 0 200 200`}
                  className="justify-center"
                />
              </div>
              <DialogDescription className="text-base text-gray-600">
                3. Or input 2FA details manually with your email as "Account details" and code below
                as "Key"
              </DialogDescription>
              <div className="text-center">
                <p className="text-lg font-semibold">{userOtpFeature?.data.otp_secret}</p>
              </div>
              <hr />
              <DialogFooter>
                <div className="grid w-full grid-cols-2 items-center justify-between gap-2">
                  <Button
                    className="bg-brand-blue hover:bg-brand-blue/90 w-full cursor-pointer"
                    onClick={() => copyToClipboard(userOtpFeature?.data.otp_secret || '')}
                  >
                    Copy 2FA Secret
                  </Button>
                  <Button
                    className="w-full cursor-pointer"
                    variant={'destructive'}
                    onClick={hanldeClose}
                  >
                    Close
                  </Button>
                </div>
              </DialogFooter>
            </div>
          )}
          {isEnabled && !generateNew && (
            <div className="space-y-6 text-center">
              <DialogTitle className="text-brand-dark text-3xl font-bold">
                Regenerate Two-Factor Authentication (2FA)
              </DialogTitle>
              <DialogDescription className="text-base text-gray-600">
                This action will replace your current 2FA and generate a new QR code for your
                authenticator app (e.g. Google Authenticator or Authy).
              </DialogDescription>
              <DialogDescription className="text-base text-gray-600">
                Use this if you no longer have access to your previous device.
              </DialogDescription>
              <hr />
              <DialogFooter>
                <div className="grid w-full grid-cols-2 items-center justify-between gap-2">
                  <Button
                    className="bg-brand-blue hover:bg-brand-blue/90 w-full cursor-pointer"
                    onClick={() => {
                      handleGenerateNew();
                      setGenerateNew(true);
                    }}
                    disabled={useEnableMutation.isPending}
                  >
                    {useEnableMutation.isPending ? 'On Progress...' : 'Generate New'}
                  </Button>
                  <Button
                    className="w-full cursor-pointer"
                    variant={'destructive'}
                    onClick={hanldeClose}
                  >
                    Cancel
                  </Button>
                </div>
              </DialogFooter>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Modal2FA;
